import {
  basketAction,
  basketArtistIdSelector,
  basketSelector,
  createBasketProductListSelector,
  getEnvVariable,
  lookFromLastItemWasAddedSelector,
  TUseCheckoutFlowType,
} from '@elc/common';
import { takeWhile } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';
import { pages } from '../routes/config';
import { artistSelector, promoCodeSelector } from '../store/selectors/artist.selectors';
import { locationSelector } from '../store/selectors/location.selectors';
import { removeEmpty } from '../utils/removeEmpty';
import { addQueryParamToUrl } from '../utils/addQueryParamToUrl';

// DO NOT SKIP www, without it aveda will redirect to www. and double encode parameters
const URL =
  getEnvVariable('BUILD_TYPE') === 'production'
    ? 'https://www.aveda.com'
    : 'https://e.aveda.na.us.stage.ncsa.elcdev.net';

const removeProtocol = (url: string): string =>
  url.startsWith('http://') ? url.replace('http://', '') : url.replace('https://', '');

const removeQueryParams = (url: string): string => takeWhile(url, (char) => char !== '?').join('');

const prepareUrl = (url: string): string => removeProtocol(removeQueryParams(url));

const objectToQueryParams = (obj: Record<string, string>, withEncoding = false): string =>
  Object.entries(obj)
    .map(([key, value]) => {
      const shouldEncode = withEncoding && key !== 'products';
      return `${key}=${shouldEncode ? encodeURIComponent(value) : value}`;
    })
    .join('&');

const addQueryParamToUrlWithCondition = (condition: boolean, param: string): Promise<void> => {
  if (condition) {
    return addQueryParamToUrl(param);
  }
  return Promise.resolve();
};

export const useAvedaRedirect = (): TUseCheckoutFlowType => {
  let redirectUrl = '';
  const dispatch = useDispatch();
  const products = useSelector(createBasketProductListSelector('sku'));
  const basket = useSelector(basketSelector);
  const salon = useSelector(locationSelector);
  const artist = useSelector(artistSelector);
  const promoCode = useSelector(promoCodeSelector);
  const lookFromLastItemWasAdded = useSelector(lookFromLastItemWasAddedSelector);
  const artistId = useSelector(basketArtistIdSelector);

  const isArtistPage = useMatch(pages.ARTIST) !== null;
  const isSalonPage = useMatch(pages.LOCATION) !== null;
  const isLookSalonPage = useMatch(pages.LOOK_FROM_LOCATION) !== null;
  const isLookFromArtistPage = useMatch(pages.LOOK_FROM_ARTIST) !== null;
  const isSalonProductPage = useMatch(pages.PRODUCT_FROM_LOCATION) !== null;

  const artistID =
    isArtistPage && Array.isArray(artistId) ? artist.publishing_id || artist.artist_id : artistId;

  return (args = {}) => {
    const { path = URL, source = 'checkout', clearCart = true } = args;

    const endpoint = path.startsWith('/') ? `${URL}${path}` : path;
    const shouldWelcomeParamAppend = !!products.length;
    const welcomeQueryParam = shouldWelcomeParamAppend ? '?welcome=true' : '';

    // eslint-disable-next-line
    const queryParams: any = {
      products: '',
      label: isSalonPage || isLookSalonPage ? salon.name : artist.name,
      SalonID: '',
      redirect: `${prepareUrl(window.location.href)}${welcomeQueryParam}`,
      socialshoppe: '1',
      artistID,
      promoCode: '',
      lookid: lookFromLastItemWasAdded,
    };

    if (salon?.businessId || artist?.connected_location?.businessId) {
      queryParams.SalonID = salon.businessId || artist?.connected_location?.businessId;
    }

    if (!isSalonPage && !isLookSalonPage && !isArtistPage && !isSalonProductPage) {
      const baseUrl = takeWhile(prepareUrl(window.location.href), (char) => char !== '/').join('');
      queryParams.redirect = `${baseUrl}/a/${artist.vanity_url}${welcomeQueryParam}`;
    }

    if (isLookSalonPage || isSalonProductPage) {
      const baseUrl = takeWhile(prepareUrl(window.location.href), (char) => char !== '/').join('');
      queryParams.redirect = `${baseUrl}/salon/${salon.vanity_url}${welcomeQueryParam}`;
    }

    if ((isArtistPage || isLookFromArtistPage) && promoCode) {
      queryParams.promoCode = promoCode;
    }

    queryParams.products = products.length
      ? products
          .map((product: { [key: string]: number }): string => {
            const value = Object.values(product)[0];
            const key = Object.keys(product)[0];

            return Array(value).fill(key).join();
          })
          .join(',')
      : '';
    if (clearCart) {
      dispatch(basketAction.checkout({ basket, source }));
      dispatch(basketAction.clearBasket());
    }

    // TODO : delete production condition before release

    redirectUrl = `${endpoint}?${objectToQueryParams(removeEmpty(queryParams), true).toString()}`;

    // eslint-disable-next-line
    console.log({ redirectUrl });

    addQueryParamToUrlWithCondition(shouldWelcomeParamAppend, 'welcome=true').then(() =>
      window.open(redirectUrl, '_self'),
    );
    dispatch(basketAction.isPendingBasket(false));
  };
};
